import { useState } from 'react';
import { useWhatsappWebStatus } from '../whatsappWebApi';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ShieldAlert, QrCode, Info } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useWhatsappWebMutations } from '../whatsappWebApi';
import { toast } from 'react-hot-toast';
import { ChatSidebar } from './ChatSidebar';
import { ChatWindow } from './ChatWindow';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

export function WhatsappFullClient({ canManage }: { canManage: boolean }) {
  const { data: state, isLoading } = useWhatsappWebStatus();
  const { connect, disconnect } = useWhatsappWebMutations();
  const [selectedChat, setSelectedChat] = useState<string | null>(null);

  if (isLoading || !state) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const isReady = state.status === 'ready';

  return (
    <div className="flex h-[80vh] min-h-[600px] w-full flex-col overflow-hidden bg-surface md:flex-row border-t border-outline">
      {!isReady ? (
        <div className="flex h-full w-full flex-col items-center justify-center bg-[#f0f2f5] p-8">
          <div className="max-w-md text-center bg-white p-10 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-display-sm font-light text-[#41525d] mb-4">WhatsApp Web Connection</h2>
            
            <div className="mb-8 flex gap-3 rounded-lg bg-warning/10 p-4 text-left border-l-4 border-warning/40">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning-text" aria-hidden />
              <div className="text-body-sm text-warning-text">
                <p className="font-medium">Unofficial connection — use with care</p>
                <p className="mt-1">
                  This links a normal WhatsApp number through WhatsApp Web. Meta's terms do not permit
                  unofficial clients, and linked numbers can be rate-limited or banned. For a fully
                  compliant channel, use the WhatsApp Business API instead.
                </p>
              </div>
            </div>

            {state.status === 'auth_failure' && (
              <p className="mb-2 text-body-sm font-medium text-[#41525d]">
                The phone ended this session — maybe it was removed from Linked Devices, or was offline too long.
              </p>
            )}
            {state.status === 'error' && (
              <p className="mb-2 text-body-sm font-medium text-[#41525d]">
                Couldn't start the WhatsApp session. This is usually temporary — try again in a moment.
              </p>
            )}

            {state.lastError && (
              <p className="mb-4 rounded-md bg-danger/10 p-3 text-body-sm text-danger-text font-medium text-left">{state.lastError}</p>
            )}

            {state.status === 'qr' && state.qr && (
              <div className="mx-auto flex flex-col items-center gap-4 mb-6">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                  <img src={state.qr} alt="WhatsApp Web QR code" className="h-64 w-64 rounded-md" />
                </div>
                <p className="text-body-sm text-[#54656f] max-w-[280px]">
                  On your phone open WhatsApp → <strong>Linked devices</strong> → <strong>Link a device</strong>,
                  then scan this code.
                </p>
              </div>
            )}

            {(state.status === 'starting' || state.status === 'authenticated') && (
              <div className="mb-6 flex flex-col items-center gap-3">
                <Spinner className="text-[#00a884]" />
                <p className="text-body-md text-[#54656f] font-medium">Connecting to WhatsApp...</p>
              </div>
            )}

            {canManage ? (
              <div className="flex justify-center gap-3">
                {state.status !== 'ready' && (
                  <Button
                    disabled={connect.isPending || state.status === 'starting'}
                    onClick={() =>
                      connect.mutate({ restart: state.status === 'qr' || state.status === 'auth_failure' || state.status === 'error' }, {
                        onError: (err) => toast.error(errorMessage(err, 'Could not start the session.')),
                      })
                    }
                    size="lg"
                    className="bg-[#00a884] hover:bg-[#017561] text-white rounded-full px-6"
                  >
                    <QrCode className="h-5 w-5 mr-2" aria-hidden />
                    {state.status === 'qr'
                      ? 'Refresh QR Code'
                      : state.status === 'auth_failure' || state.status === 'error'
                        ? 'Try Again'
                        : 'Link a Number'}
                  </Button>
                )}
                {(state.status === 'qr' || state.status === 'authenticated') && (
                  <Button
                    variant="outline"
                    disabled={disconnect.isPending}
                    onClick={() =>
                      disconnect.mutate(undefined, {
                        onSuccess: () => toast.success('Disconnected.'),
                      })
                    }
                    size="lg"
                    className="rounded-full px-6 text-[#54656f] hover:bg-[#f0f2f5] border-none"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-body-sm text-[#54656f]">
                Only administrators can link or disconnect the WhatsApp number.
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="w-full md:w-[380px] lg:w-[420px] shrink-0 bg-white flex flex-col h-full border-r border-[#d1d7db]">
            <ChatSidebar 
              selectedChat={selectedChat} 
              onSelectChat={setSelectedChat} 
              onDisconnect={() => disconnect.mutate(undefined, {
                onSuccess: () => {
                  toast.success('WhatsApp disconnected.');
                  setSelectedChat(null);
                }
              })}
              me={state.me}
            />
          </div>
          <div className="flex-1 bg-[#f0f2f5] h-full overflow-hidden relative">
            {selectedChat ? (
              <ChatWindow chatId={selectedChat} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-center p-8 bg-[#f0f2f5] border-b-[6px] border-[#43c561]">
                <div className="w-[450px] max-w-full">
                  <div className="mx-auto mb-10 w-full flex items-center justify-center">
                    <img 
                      src="https://static.whatsapp.net/rsrc.php/v3/y6/r/wa669aeOtdl.png" 
                      alt="WhatsApp Web" 
                      className="h-auto w-80 object-contain"
                      onError={(e) => {
                        // Fallback if the image doesn't load
                        e.currentTarget.src = "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg";
                        e.currentTarget.className = "h-24 w-24 opacity-20 grayscale";
                      }}
                    />
                  </div>
                  <h3 className="text-[32px] font-light text-[#41525d] mb-4">WhatsApp Web</h3>
                  <p className="text-[14px] text-[#667781] leading-[20px]">
                    Send and receive messages without keeping your phone online.<br/>
                    Use WhatsApp on up to 4 linked devices and 1 phone at the same time.
                  </p>
                  <div className="mt-16 flex items-center justify-center gap-1.5 text-[12px] text-[#8696a0]">
                    <Info className="h-3 w-3 text-[#8696a0]" aria-hidden />
                    <span>Messages here are logged for your team's records</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
